import type { HostCapabilities } from "../../capabilities";
import { createScreensHost } from "./host";
import type { MarlinStoreDb } from "../infra/db";
import * as ops from "./store";

const windowApi = (db: MarlinStoreDb, screenId: number) => ({
  getScreenLayouts: () => ops.getScreenLayouts(db),
  getScreenLayout: () => ops.getScreenLayout(db, screenId),
  getScreens: () => ops.getScreens(db),
  getScreen: () => ops.getScreen(db, screenId),
  getOverlays: () => ops.getOverlays(db, screenId),
  getGridData: () => ops.getGridData(db, screenId),
  getPositionData: (position: Parameters<typeof ops.getPositionData>[2]) =>
    ops.getPositionData(db, screenId, position),
  getPinData: (position: Parameters<typeof ops.getPinData>[2]) =>
    ops.getPinData(db, screenId, position),
  getAllPins: () => ops.getAllPins(db),
  getCamerasOnPinnedCells: () => ops.getCamerasOnPinnedCells(db),
  getFeedData: () => ops.getFeedData(db, screenId),
  setScreens: (screens: Parameters<typeof ops.setScreens>[1]) =>
    ops.setScreens(db, screens),
  updateScreenType: (screenType: Parameters<typeof ops.updateScreenType>[2]) =>
    ops.updateScreenType(db, screenId, screenType),
  resetScreenTypes: () => ops.resetScreenTypes(db),
  setFullScreen: (position: Parameters<typeof ops.setFullScreen>[2]) =>
    ops.setFullScreen(db, screenId, position),
  setHalfScreen: (position: Parameters<typeof ops.setHalfScreen>[2]) =>
    ops.setHalfScreen(db, screenId, position),
  setHalfScreenLeft: (position: Parameters<typeof ops.setHalfScreenLeft>[2]) =>
    ops.setHalfScreenLeft(db, screenId, position),
  setHalfScreenRight: (
    position: Parameters<typeof ops.setHalfScreenRight>[2],
  ) => ops.setHalfScreenRight(db, screenId, position),
  clearPosition: (position: Parameters<typeof ops.clearPosition>[2]) =>
    ops.clearPosition(db, screenId, position),
  setGridLayout: (gridLayout: Parameters<typeof ops.setGridLayout>[2]) =>
    ops.setGridLayout(db, screenId, gridLayout),
  setNCams: (nCams: Parameters<typeof ops.setNCams>[2]) =>
    ops.setNCams(db, screenId, nCams),
  setVideoAspectRatio: (ratio: Parameters<typeof ops.setVideoAspectRatio>[2]) =>
    ops.setVideoAspectRatio(db, screenId, ratio),
  setPositionData: (
    position: Parameters<typeof ops.setPositionData>[2],
    data: Parameters<typeof ops.setPositionData>[3],
  ) => ops.setPositionData(db, screenId, position, data),
  setPinData: (
    position: Parameters<typeof ops.setPinData>[2],
    pin: Parameters<typeof ops.setPinData>[3],
  ) => ops.setPinData(db, screenId, position, pin),
  swapCamerasInPosition: (
    position: Parameters<typeof ops.swapCamerasInPosition>[2],
    sourceCam: Parameters<typeof ops.swapCamerasInPosition>[3],
    targetCam: Parameters<typeof ops.swapCamerasInPosition>[4],
  ) => ops.swapCamerasInPosition(db, screenId, position, sourceCam, targetCam),
  cycleCamerasInPosition: (
    position: Parameters<typeof ops.cycleCamerasInPosition>[2],
  ) => ops.cycleCamerasInPosition(db, screenId, position),
  cycleToNextPen: () => ops.cycleToNextPen(db, screenId),
  cycleToPreviousPen: () => ops.cycleToPreviousPen(db, screenId),
  loadMachines: (
    machinesToLoad: Parameters<typeof ops.loadMachines>[1],
    options?: Parameters<typeof ops.loadMachines>[2],
  ) => ops.loadMachines(db, machinesToLoad, options),
  updateFeedSites: (sites: Parameters<typeof ops.updateFeedSites>[2]) =>
    ops.updateFeedSites(db, screenId, sites),
  updateFeedColumns: (columns: Parameters<typeof ops.updateFeedColumns>[2]) =>
    ops.updateFeedColumns(db, screenId, columns),
  updateCopilotFeedColumns: (
    columns: Parameters<typeof ops.updateCopilotFeedColumns>[2],
  ) => ops.updateCopilotFeedColumns(db, screenId, columns),
  updateFeedFilters: (filters: Parameters<typeof ops.updateFeedFilters>[2]) =>
    ops.updateFeedFilters(db, screenId, filters),
});

export const createScreensApi = (
  db: MarlinStoreDb,
  screenId: number,
  capabilities: Pick<HostCapabilities, "screens" | "windows">,
) => ({
  ...windowApi(db, screenId),
  ...createScreensHost(db, capabilities, screenId),
  forScreen: (id: number) => windowApi(db, id),
});

export type ScreensApi = ReturnType<typeof createScreensApi>;
